import { Button } from "$components/button/button";
import { CardColors, CardFooter, CardFooterItem } from "$components/card";
import { FormattedDate } from "$components/date/formatted-date";
import { CheckIcon, SmartRecommendationIcon } from "$components/icons";
import { useLazyUser } from "$global-providers/user-provider";
import { randomId } from "$routes/_lib/random-id";
import {
  CardHeader,
  PatientHistoryCard,
} from "$routes/patient/_lib/history-card";
import type { HistoricReferral } from "$routes/patient/[:patientId]/overview/history/_lib/types";
import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  styled,
  Typography,
} from "@material-ui/core";
import { addDays, compareDesc, subDays } from "date-fns";
import { ReferralFeedbackType, Role } from "elephant-web/gql";
import { useEffect, useReducer } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useRxData } from "rxdb-hooks";
import type { ReferralFeedback } from "../../../../global-providers/rxdb-provider/collections/referral-feedback";
import {
  RXDB_REFERRAL_FEEDBACK_COLLECTION_NAME,
  useRxCollectionReferralFeedback,
} from "../../../../global-providers/rxdb-provider/collections/referral-feedback";
import { useHistoryUserDetailsQuery } from "./__generated__/user-details.gql.gql";
import { isCrossOrgAccessError } from "./is-cross-org-access-error";

const ContentArea = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  paddingTop: theme.spacing(2),
  marginBottom: theme.spacing(-2), // negative margin to reduce excessive footer spacing from PatientHistoryCard
  flex: 1, // allows the recent scans section to grow
}));

const RecentScansSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  backgroundColor: theme.palette.grey[50],
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const ScanLink = styled("button")(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: "none",
  fontWeight: 700,
  fontSize: "16px",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  textAlign: "left",
  "&:hover": {
    textDecoration: "underline",
  },
}));

const ScansSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const StyledSmartRecommendationIcon = styled(SmartRecommendationIcon)({
  width: 20,
  height: 20,
});

const FeedbackSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
}));

const FeedbackHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

const FeedbackLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 14,
  fontWeight: 400,
}));

const FeedbackQuestion = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 16,
  fontWeight: 700,
}));

const FeedbackButtons = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  flexWrap: "wrap",
}));

const FeedbackSentContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  height: 44,
}));

const FeedbackSentText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 16,
  fontWeight: 400,
}));

const StyledCheckIcon = styled(CheckIcon)({
  width: 24,
  height: 24,
});

const FEEDBACK_TIMING = {
  SPINNER_DISPLAY_DURATION: 600,
  SUCCESS_DISPLAY_DURATION: 2800,
  COLLAPSE_DURATION: 500,
} as const;

const userDisplayMessages = defineMessages({
  differentFacility: {
    id: "referral-history-card.footer.by.differentFacility",
    defaultMessage: "A different facility",
  },
  unknown: {
    id: "referral-history-card.footer.by.unknown",
    defaultMessage: "Unknown",
  },
});

type FeedbackStage =
  | "HIDDEN"
  | "SHOWING_BUTTONS"
  | "SHOWING_SPINNER"
  | "SHOWING_SUCCESS"
  | "COLLAPSING";

type FeedbackState = {
  stage: FeedbackStage;
};

type FeedbackAction =
  | { type: "SHOW_BUTTONS" }
  | { type: "HIDE" }
  | { type: "SUBMIT_FEEDBACK" }
  | { type: "TRANSITION_TO_SUCCESS" }
  | { type: "START_COLLAPSE" }
  | { type: "COMPLETE_COLLAPSE" };

function feedbackReducer(
  state: FeedbackState,
  action: FeedbackAction,
): FeedbackState {
  switch (action.type) {
    case "SHOW_BUTTONS":
      return { stage: "SHOWING_BUTTONS" };
    case "HIDE":
      return { stage: "HIDDEN" };
    case "SUBMIT_FEEDBACK":
      return { stage: "SHOWING_SPINNER" };
    case "TRANSITION_TO_SUCCESS":
      return { stage: "SHOWING_SUCCESS" };
    case "START_COLLAPSE":
      return { stage: "COLLAPSING" };
    case "COMPLETE_COLLAPSE":
      return { stage: "HIDDEN" };
    default:
      return state;
  }
}

const ReferralFeedbackContent: React.FC<{
  feedbackStage: FeedbackStage;
  onFeedbackClick: (feedbackType: ReferralFeedback["feedbackType"]) => void;
}> = ({ feedbackStage, onFeedbackClick }) => {
  return (
    <Collapse
      in={feedbackStage !== "COLLAPSING" && feedbackStage !== "HIDDEN"}
      timeout={FEEDBACK_TIMING.COLLAPSE_DURATION}
      unmountOnExit
    >
      <div>
        <Divider />
        <FeedbackSection>
          <FeedbackHeader>
            <FeedbackLabel>
              <FormattedMessage
                id="referral-history-card.feedback.helpImprove"
                defaultMessage="Help our sonographers improve"
              />
            </FeedbackLabel>
            <FeedbackQuestion>
              <FormattedMessage
                id="referral-history-card.feedback.question"
                defaultMessage="Which best describes this referral?"
              />
            </FeedbackQuestion>
          </FeedbackHeader>
          {feedbackStage === "SHOWING_SPINNER" && (
            <FeedbackSentContainer>
              <CircularProgress size={16} thickness={5} color="primary" />
            </FeedbackSentContainer>
          )}
          {(feedbackStage === "SHOWING_SUCCESS" ||
            feedbackStage === "COLLAPSING") && (
            <FeedbackSentContainer>
              <StyledCheckIcon />
              <FeedbackSentText>
                <FormattedMessage
                  id="referral-history-card.feedback.sent"
                  defaultMessage="Feedback sent"
                />
              </FeedbackSentText>
            </FeedbackSentContainer>
          )}
          {feedbackStage === "SHOWING_BUTTONS" && (
            <FeedbackButtons>
              <Button
                variant="outlined"
                color="primary"
                onClick={() =>
                  onFeedbackClick(ReferralFeedbackType.Appropriate)
                }
              >
                <FormattedMessage
                  id="referral-history-card.feedback.appropriate"
                  defaultMessage="Appropriate"
                />
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() =>
                  onFeedbackClick(ReferralFeedbackType.WrongFacility)
                }
              >
                <FormattedMessage
                  id="referral-history-card.feedback.wrongFacility"
                  defaultMessage="Sent to wrong facility"
                />
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => onFeedbackClick(ReferralFeedbackType.NotNeeded)}
              >
                <FormattedMessage
                  id="referral-history-card.feedback.notNeeded"
                  defaultMessage="Maybe not needed"
                />
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() =>
                  onFeedbackClick(ReferralFeedbackType.InsufficientDetail)
                }
              >
                <FormattedMessage
                  id="referral-history-card.feedback.insufficientDetail"
                  defaultMessage="Not enough detail"
                />
              </Button>
            </FeedbackButtons>
          )}
        </FeedbackSection>
      </div>
    </Collapse>
  );
};

export const ReferralHistoryCard: React.FC<{
  referral: HistoricReferral;
}> = ({ referral }) => {
  const { formatMessage } = useIntl();
  const { data: referralUser, error: userError } = useHistoryUserDetailsQuery({
    variables: { userId: referral.meta.createdByUserId },
  });

  const user = useLazyUser();
  const feedbackCollection = useRxCollectionReferralFeedback();

  const facilityName = referral.facilityName || "Unknown facility";
  const { result: existingFeedback, isFetching: isFetchingFeedback } =
    useRxData<ReferralFeedback>(
      RXDB_REFERRAL_FEEDBACK_COLLECTION_NAME,
      (collection) =>
        collection?.find({
          selector: {
            referralId: referral.id,
          },
        }),
    );

  const hasFeedback = existingFeedback && existingFeedback.length > 0;
  const [state, dispatch] = useReducer(feedbackReducer, { stage: "HIDDEN" });

  useEffect(() => {
    // Don't interfere with user interaction states
    if (
      isFetchingFeedback ||
      state.stage === "SHOWING_SPINNER" ||
      state.stage === "SHOWING_SUCCESS" ||
      state.stage === "COLLAPSING"
    ) {
      return;
    }

    if (user?.role === Role.Sonographer || hasFeedback) {
      dispatch({ type: "HIDE" });
      return;
    }

    // Show buttons only when confirmed no feedback
    dispatch({ type: "SHOW_BUTTONS" });
  }, [state.stage, hasFeedback, isFetchingFeedback, user?.role]);

  const handleFeedbackClick = async (
    feedbackType: ReferralFeedback["feedbackType"],
  ) => {
    if (!feedbackCollection || !user?.id) return;

    dispatch({ type: "SUBMIT_FEEDBACK" });

    try {
      const now = new Date().toISOString();
      await feedbackCollection.insert({
        id: randomId(),
        referralId: referral.id,
        patientId: referral.patientId,
        feedbackType,
        meta: {
          createdAt: now,
          createdByUserId: user.id,
          updatedAt: now,
          updatedByUserId: user.id,
        },
      });
    } catch (error) {
      dispatch({ type: "SHOW_BUTTONS" });
    }
  };

  // Transition: SHOWING_SPINNER → SHOWING_SUCCESS
  useEffect(() => {
    if (state.stage !== "SHOWING_SPINNER") {
      return;
    }

    const transitionTimer = window.setTimeout(() => {
      dispatch({ type: "TRANSITION_TO_SUCCESS" });
    }, FEEDBACK_TIMING.SPINNER_DISPLAY_DURATION);

    return () => window.clearTimeout(transitionTimer);
  }, [state.stage]);

  // Transition: SHOWING_SUCCESS → COLLAPSING
  useEffect(() => {
    if (state.stage !== "SHOWING_SUCCESS") {
      return;
    }

    const hideTimer = window.setTimeout(() => {
      dispatch({ type: "START_COLLAPSE" });
    }, FEEDBACK_TIMING.SUCCESS_DISPLAY_DURATION);

    return () => window.clearTimeout(hideTimer);
  }, [state.stage]);

  // Transition: COLLAPSING → HIDDEN
  useEffect(() => {
    if (state.stage !== "COLLAPSING") {
      return;
    }

    const hiddenTimer = window.setTimeout(() => {
      dispatch({ type: "COMPLETE_COLLAPSE" });
    }, FEEDBACK_TIMING.COLLAPSE_DURATION);

    return () => window.clearTimeout(hiddenTimer);
  }, [state.stage]);

  const fromOneWeekEitherSideStudies = referral.allImagingStudies
    .filter((study) => {
      const studyDate = new Date(study.meta.createdAt);
      const referralDate = new Date(referral.meta.createdAt);
      return (
        studyDate >= subDays(referralDate, 7) &&
        studyDate <= addDays(referralDate, 7)
      );
    })
    .sort((a, b) =>
      compareDesc(new Date(a.meta.createdAt), new Date(b.meta.createdAt)),
    );

  return (
    <PatientHistoryCard
      color={CardColors.REFERRAL}
      expanded
      HeaderComponent={() => (
        <CardHeader
          color={CardColors.REFERRAL}
          category="Referral"
          title={"Obstetric"}
          id={referral.id}
        />
      )}
      FooterComponent={() => (
        <CardFooter>
          <CardFooterItem
            label={
              <FormattedMessage
                id="referral-history-card.footer.requestedOn"
                defaultMessage="Requested:"
              />
            }
          >
            <FormattedDate value={referral.meta.createdAt} showTime />
          </CardFooterItem>
          <CardFooterItem label="By:">
            {isCrossOrgAccessError(userError)
              ? formatMessage(userDisplayMessages.differentFacility)
              : userError
                ? formatMessage(userDisplayMessages.unknown)
                : referralUser?.user?.person?.firstname
                  ? [
                      referralUser.user.person.firstname,
                      referralUser.user.person.lastname,
                    ]
                      .filter(Boolean)
                      .join(" ")
                  : formatMessage(userDisplayMessages.unknown)}
          </CardFooterItem>
        </CardFooter>
      )}
      PostFooterSection={
        <ReferralFeedbackContent
          feedbackStage={state.stage}
          onFeedbackClick={handleFeedbackClick}
        />
      }
    >
      <ContentArea>
        <div>
          <Typography variant="body1" color="textSecondary">
            <FormattedMessage
              id="referral-history-card.referredTo"
              defaultMessage="Referred to"
            />
          </Typography>
          <Typography variant="h6" color="textPrimary">
            {facilityName}
          </Typography>
        </div>

        <div>
          <Typography variant="body1" color="textSecondary">
            <FormattedMessage
              id="referral-history-card.reason"
              defaultMessage="Reason for referral"
            />
          </Typography>
          <Typography variant="h6" color="textPrimary">
            {referral.reasons.join(", ")}
          </Typography>
        </div>

        {referral.notes && (
          <Box>
            <Typography variant="body1" color="textSecondary">
              <FormattedMessage
                id="referral-history-card.notes"
                defaultMessage="Notes"
              />
            </Typography>
            <Typography variant="h6" color="textPrimary">
              {referral.notes}
            </Typography>
          </Box>
        )}

        {fromOneWeekEitherSideStudies.length > 0 && (
          <RecentScansSection>
            <ScansSectionTitle variant="subtitle1" color="textPrimary">
              <FormattedMessage
                id="referral-history-card.recentScans"
                defaultMessage="Recent obstetric ultrasound scans"
              />
              <StyledSmartRecommendationIcon />
            </ScansSectionTitle>
            {fromOneWeekEitherSideStudies.map((study) => (
              <ScanLink
                key={study.id}
                onClick={() => {
                  const targetElement = document.getElementById(
                    `imagingCard${study.id}`,
                  );
                  if (targetElement) {
                    targetElement.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                <FormattedDate value={study.meta.createdAt} showTime />
              </ScanLink>
            ))}
          </RecentScansSection>
        )}
      </ContentArea>
    </PatientHistoryCard>
  );
};
